/**
 * Runtime Asset Version Checker
 *
 * Detects when a new deployment has occurred by comparing the currently loaded
 * main bundle asset with the current main bundle path in asset-manifest.json on the server.
 *
 * How it works:
 * 1. On app load, identifies the currently loaded versioned main bundle
 * 2. Periodically fetches asset-manifest.json from the server
 * 3. Compares the current bundle path with the server's bundle path
 * 4. If mismatch detected, shows a notification prompting user to refresh
 * 5. User can refresh immediately or click "Later" to be reminded in 5 minutes
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const REMIND_LATER_DELAY_MS = 5 * 60 * 1000; // 5 minutes after dismiss
const INITIAL_CHECK_DELAY_MS = 5 * 1000; // 5 seconds

// Singleton guard to prevent multiple initializations
let initialized = false;

// Test-only hook for clearing module state across runners.
export function __resetAssetVersionCheckerForTests() {
  initialized = false;
}

/**
 * Normalizes an asset path or URL to a same-origin pathname for comparison.
 * @param {string} assetPath
 * @returns {string|null}
 */
function normalizeAssetPath(assetPath) {
  if (!assetPath) {
    return null;
  }

  try {
    return new URL(assetPath, window.location.origin).pathname;
  } catch (error) {
    console.warn('[AssetVersionChecker] Could not normalize asset path:', assetPath);
    return null;
  }
}

/**
 * Checks if a path looks like a versioned app entry bundle produced by CRA or Vite.
 * @param {string} assetPath
 * @returns {boolean}
 */
function isVersionedMainBundlePath(assetPath) {
  if (!assetPath) {
    return false;
  }

  return (
    /\/static\/js\/main\.[^/]+\.js$/.test(assetPath) ||
    /\/assets\/(?:index|main)-[^/]+\.js$/.test(assetPath)
  );
}

/**
 * Extracts a short version label from a versioned main bundle path for logging.
 * @param {string} assetPath
 * @returns {string|null}
 */
function getBundleVersionLabel(assetPath) {
  if (!assetPath) {
    return null;
  }

  const fileName = assetPath.split('/').pop() || assetPath;
  const craMatch = fileName.match(/^main\.([^.]+)\.js$/);

  if (craMatch && craMatch[1]) {
    return craMatch[1];
  }

  const viteMatch = fileName.match(/^(?:index|main)-([^.]+)\.js$/);

  if (viteMatch && viteMatch[1]) {
    return viteMatch[1];
  }

  return fileName;
}

/**
 * Finds the currently loaded versioned main JavaScript bundle.
 * @returns {string|null}
 */
function getCurrentMainBundlePath() {
  try {
    const scripts = Array.from(document.scripts);
    const mainScript = scripts.find((script) => {
      const src = normalizeAssetPath(script.src || '');
      return isVersionedMainBundlePath(src);
    });

    if (!mainScript || !mainScript.src) {
      console.warn('[AssetVersionChecker] Main bundle script not found');
      return null;
    }

    return normalizeAssetPath(mainScript.src);
  } catch (error) {
    console.error('[AssetVersionChecker] Error locating current main bundle:', error);
    return null;
  }
}

/**
 * Fetches the asset manifest from the server and extracts the versioned main bundle path.
 * @returns {Promise<string|null>}
 */
async function getServerMainBundlePath() {
  try {
    // Add cache-busting query parameter to ensure we get the latest version
    // Use absolute path from current origin to avoid path issues
    const manifestUrl = `${window.location.origin}/asset-manifest.json?t=${Date.now()}`;
    
    const response = await fetch(manifestUrl, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        console.warn('[AssetVersionChecker] asset-manifest.json not found (404)');
        return null;
      }
      throw new Error(`Failed to fetch manifest: ${response.status} ${response.statusText}`);
    }
    
    const manifest = await response.json();
    
    // Manifest structure: { "files": { "main.js": "/assets/index-<hash>.js", ... } }
    const mainJsPath =
      manifest.files?.['main.js'] ||
      manifest.entrypoints?.find((entry) => entry.endsWith('.js'));
    
    if (!mainJsPath) {
      console.warn('[AssetVersionChecker] main.js not found in manifest');
      return null;
    }

    const normalizedMainBundlePath = normalizeAssetPath(mainJsPath);

    if (!isVersionedMainBundlePath(normalizedMainBundlePath)) {
      console.warn(
        '[AssetVersionChecker] Could not determine versioned main bundle path from manifest:',
        mainJsPath
      );
      return null;
    }

    return normalizedMainBundlePath;
  } catch (error) {
    // Network errors should not trigger reload - user might be offline
    console.error('[AssetVersionChecker] Error fetching server bundle path:', error);
    return null;
  }
}

/**
 * Shows an update notification banner to the user
 * Uses inline styles to avoid Tailwind purging issues in production
 * @param {Function} onDismiss - Callback when user dismisses the notification
 */
function showUpdateNotification(onDismiss) {
  // Don't show if already showing
  if (document.getElementById('update-notification')) {
    return;
  }

  // Create container
  const container = document.createElement('div');
  container.id = 'update-notification';
  container.style.cssText = `
    position: fixed;
    top: 0;
    right: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding: 24px;
    pointer-events: none;
  `;

  // Create notification card
  const card = document.createElement('div');
  card.style.cssText = `
    width: 100%;
    max-width: 24rem;
    overflow: hidden;
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    pointer-events: auto;
  `;

  // Create inner content
  const inner = document.createElement('div');
  inner.style.cssText = 'padding: 16px;';

  const flexContainer = document.createElement('div');
  flexContainer.style.cssText = 'display: flex; align-items: flex-start;';

  // Icon
  const iconDiv = document.createElement('div');
  iconDiv.style.cssText = 'flex-shrink: 0;';
  iconDiv.innerHTML = `
    <svg style="width: 24px; height: 24px; color: #0BA4EB;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
    </svg>
  `;

  // Message container
  const messageDiv = document.createElement('div');
  messageDiv.style.cssText = 'margin-left: 12px; flex: 1; padding-top: 2px;';

  const title = document.createElement('p');
  title.style.cssText = 'font-size: 14px; font-weight: 600; color: #111827; margin: 0;';
  title.textContent = 'Update Available';

  const content = document.createElement('p');
  content.style.cssText = 'margin-top: 4px; font-size: 14px; color: #6b7280; margin-bottom: 0;';
  content.textContent = 'A new version is ready. Refresh to get the latest features.';

  // Action buttons
  const actionsDiv = document.createElement('div');
  actionsDiv.style.cssText = 'margin-top: 12px; display: flex; gap: 8px;';

  const refreshBtn = document.createElement('button');
  refreshBtn.id = 'update-refresh-btn';
  refreshBtn.type = 'button';
  refreshBtn.style.cssText = `
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: #0BA4EB;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  `;
  refreshBtn.textContent = 'Refresh now';
  refreshBtn.onclick = () => window.location.reload();

  const separator = document.createElement('span');
  separator.style.cssText = 'color: #d1d5db;';
  separator.textContent = '|';

  const laterBtn = document.createElement('button');
  laterBtn.id = 'update-later-btn';
  laterBtn.type = 'button';
  laterBtn.style.cssText = `
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: #6b7280;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  `;
  laterBtn.textContent = 'Later';
  laterBtn.onclick = () => {
    container.remove();
    if (onDismiss) onDismiss();
  };

  // Close button
  const closeDiv = document.createElement('div');
  closeDiv.style.cssText = 'margin-left: 16px; flex-shrink: 0;';

  const closeBtn = document.createElement('button');
  closeBtn.id = 'update-close-btn';
  closeBtn.type = 'button';
  closeBtn.style.cssText = `
    display: inline-flex;
    color: #9ca3af;
    background: white;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    padding: 0;
  `;
  closeBtn.innerHTML = `
    <span style="position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); border: 0;">Close</span>
    <svg style="width: 20px; height: 20px;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
    </svg>
  `;
  closeBtn.onclick = () => {
    container.remove();
    if (onDismiss) onDismiss();
  };

  // Assemble
  actionsDiv.appendChild(refreshBtn);
  actionsDiv.appendChild(separator);
  actionsDiv.appendChild(laterBtn);

  messageDiv.appendChild(title);
  messageDiv.appendChild(content);
  messageDiv.appendChild(actionsDiv);

  closeDiv.appendChild(closeBtn);

  flexContainer.appendChild(iconDiv);
  flexContainer.appendChild(messageDiv);
  flexContainer.appendChild(closeDiv);

  inner.appendChild(flexContainer);
  card.appendChild(inner);
  container.appendChild(card);
  document.body.appendChild(container);

  console.info('[AssetVersionChecker] Update notification shown to user');
}

/**
 * Initializes the asset version checker
 * @param {Object} options - Configuration options
 * @param {number} options.checkInterval - Interval in milliseconds between checks (default: 300000 = 5 minutes)
 * @param {boolean} options.enabled - Whether the checker is enabled (default: true in production)
 */
export function initAssetVersionChecker(options = {}) {
  const {
    checkInterval = CHECK_INTERVAL_MS,
    enabled = import.meta.env.PROD,
  } = options;

  // Expose test function in development mode (always, regardless of enabled state)
  if (!import.meta.env.PROD) {
    window.__testUpdateNotification = () => {
      showUpdateNotification(() => {
        console.info('[AssetVersionChecker] Test notification dismissed');
      });
    };
    console.info('[AssetVersionChecker] Test mode: call window.__testUpdateNotification() to preview');
  }

  // Singleton guard - prevent multiple initializations
  if (initialized) {
    console.warn('[AssetVersionChecker] Already initialized. Skipping.');
    return;
  }

  // Disable in development or if explicitly disabled
  if (!enabled) {
    console.info('[AssetVersionChecker] Disabled (development mode or explicitly disabled)');
    return;
  }

  // Get the currently loaded versioned main bundle
  const currentMainBundlePath = getCurrentMainBundlePath();

  if (!currentMainBundlePath) {
    console.warn('[AssetVersionChecker] Could not determine current bundle. Checker disabled.');
    return;
  }

  const currentVersionLabel = getBundleVersionLabel(currentMainBundlePath);

  initialized = true;
  console.info(
    `[AssetVersionChecker] Initialized. Current bundle: ${
      currentVersionLabel ? `${currentVersionLabel.substring(0, 8)}...` : currentMainBundlePath
    }`
  );

  let checkTimer = null;
  let isChecking = false;
  let updateDetected = false;
  let dismissedUntil = 0;

  /**
   * Performs a version check
   */
  async function performCheck() {
    // Don't check if already checking, tab is hidden, or update already shown
    if (isChecking || document.hidden || updateDetected) {
      return;
    }

    // Don't show notification again if user dismissed recently
    if (Date.now() < dismissedUntil) {
      return;
    }

    isChecking = true;

    try {
      const serverMainBundlePath = await getServerMainBundlePath();

      // If we couldn't get the server bundle path, skip this check (might be network issue)
      if (!serverMainBundlePath) {
        return;
      }

      // Compare bundle paths
      if (currentMainBundlePath !== serverMainBundlePath) {
        const serverVersionLabel = getBundleVersionLabel(serverMainBundlePath);

        console.info(
          `[AssetVersionChecker] Version mismatch detected! ` +
          `Current: ${
            currentVersionLabel ? `${currentVersionLabel.substring(0, 8)}...` : currentMainBundlePath
          }, Server: ${
            serverVersionLabel ? `${serverVersionLabel.substring(0, 8)}...` : serverMainBundlePath
          }`
        );

        updateDetected = true;

        // Show notification to user
        showUpdateNotification(() => {
          // User clicked "Later" - remind again after delay
          updateDetected = false;
          dismissedUntil = Date.now() + REMIND_LATER_DELAY_MS;
          console.info('[AssetVersionChecker] User deferred update. Will remind in 5 minutes.');
        });
      }
    } catch (error) {
      console.error('[AssetVersionChecker] Error during version check:', error);
    } finally {
      isChecking = false;
    }
  }

  // Perform initial check after a short delay (allow app to fully load)
  setTimeout(performCheck, INITIAL_CHECK_DELAY_MS);

  // Set up periodic checks
  checkTimer = setInterval(performCheck, checkInterval);

  // Check immediately when tab becomes visible
  const visibilityHandler = () => {
    if (!document.hidden) {
      performCheck();
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  // Cleanup function (for testing or manual cleanup)
  return () => {
    if (checkTimer) {
      clearInterval(checkTimer);
    }
    document.removeEventListener('visibilitychange', visibilityHandler);
    initialized = false;
  };
}
